// Pure FEN-diff move inference for Zodiac
// This file is decoupled from the engine and only operates on FEN strings

import {
  getAlgebraicNotation,
  getPieceDisplayName,
  generateMoveDescription,
} from "./moveDescriptionUtils";

/**
 * Normalize FEN strings to handle encoding inconsistencies
 * Firebase/JSON sometimes encodes ✳️ (sparkle emoji) as * (asterisk)
 */
function normalizeFEN(fen: string): string {
  // Normalize encoding variants for consistency across FEN history
  return fen
    .replace(/\*/g, '✳️')                // ASCII ability marker -> Unicode
    .replace(/\u2620\uFE0F/g, '☣')      // ☠️ (skull+VS16) -> ☣ (biohazard)
    .replace(/\u2620(?!\uFE0F)/g, '☣'); // ☠ (skull) -> ☣ (biohazard)
  // NOTE: Do NOT normalize # -> ⚘ because # is also used in dice suffixes (#4)
}

// Improved parser: returns a 2D array of arrays of piece strings for each square
export function parseFEN(fen: string): string[][][] {
  // Remove turn indicator if present
  if (fen.startsWith("d")) {
    fen = fen.slice(1);
  }
  // Assumes FEN is in Zodiac format, columns separated by '/', bottom-to-top per column
  const columns = fen.split("/");
  const board: string[][][] = [];
  const numRows = 8; // Zodiac default
  const numCols = columns.length;
  // MODIFIERS includes both ASCII and Unicode variants for special ability markers:
  // * = ASCII asterisk, \u2733 = ✳ (eight-spoked asterisk), \uFE0F = variation selector (makes emoji)
  // ✳️ is composed of \u2733 + \uFE0F, so both must be recognized separately
  const MODIFIERS = "!@#z*$Kk\u2620\u2620\uFE0F☠\u2733✳️☣♛♕○";
  const EFFECTS = "⛓⚘"; // ⛓ = entangle, ⚘ = meadow
  for (let col = 0; col < numCols; col++) {
    const colStr = columns[col];
    let row = 0; // Start at the bottom row (row 0)
    let i = 0;
    board[col] = Array(numRows)
      .fill(null)
      .map(() => []);
    while (i < colStr.length) {
      const char = colStr[i];

      // Stacking indicator can be processed even when row >= numRows
      // because it adds to the PREVIOUS row (row - 1), not a new row
      if (char === "+") {
        // Stack: add another piece to the same square
        i++;
        let piece = "";
        // Piece letter
        if (
          i < colStr.length &&
          ((colStr[i] >= "A" && colStr[i] <= "Z") ||
          (colStr[i] >= "a" && colStr[i] <= "z"))
        ) {
          piece += colStr[i];
          i++;
          // Check for Unicode direction symbol (Penguin)
          if (i < colStr.length) {
            const nextChar = colStr[i];
            if (["↑", "↗", "→", "↘", "↓", "↙", "←", "↖"].includes(nextChar)) {
              piece += nextChar;
              i++;
            }
          }
          // Modifiers
          while (i < colStr.length && MODIFIERS.includes(colStr[i])) {
            piece += colStr[i];
            i++;
          }
          // Effects
          while (i < colStr.length && EFFECTS.includes(colStr[i])) {
            piece += colStr[i];
            i++;
          }
        }
        // Add to the previous row (same square)
        if (row > 0) {
          board[col][row - 1].push(piece);
        }
        continue;
      }

      // For pieces and empty squares, we need row < numRows
      if (row >= numRows) {
        break;
      }

      if (char >= "1" && char <= "8") {
        // Empty squares
        const count = parseInt(char, 10);
        for (let k = 0; k < count; k++) {
          board[col][row] = [];
          row++;
        }
        i++;
      } else if ((char >= "A" && char <= "Z") || (char >= "a" && char <= "z")) {
        // Piece letter, collect all following modifiers and effects
        let piece = colStr[i];
        i++;
        // Check for Unicode direction symbol (Penguin)
        if (i < colStr.length) {
          const nextChar = colStr[i];
          if (["↑", "↗", "→", "↘", "↓", "↙", "←", "↖"].includes(nextChar)) {
            piece += nextChar;
            i++;
          }
        }
        // Modifiers
        while (i < colStr.length && MODIFIERS.includes(colStr[i])) {
          piece += colStr[i];
          i++;
        }
        // Effects (e.g., ⛓) - append all effect symbols directly after the piece/modifiers
        while (i < colStr.length && EFFECTS.includes(colStr[i])) {
          piece += colStr[i];
          i++;
        }
        board[col][row] = [piece];
        row++;
      } else {
        // Unknown/special char, skip
        i++;
      }
    }
  }
  return board;
}

// Helper to extract piece type and color from FEN string
function getPieceInfo(piece: string) {
  // Uppercase = Light, lowercase = Dark
  if (!piece) return { type: "", isLight: null, isZombified: false };
  const type = piece[0].toUpperCase();
  const isLight = piece[0] === piece[0].toUpperCase();
  const isZombified = piece.includes("z");
  return { type, isLight, isZombified };
}

// Map FEN type to user-friendly name
const pieceTypeMap: Record<string, string> = {
  B: "Bear",
  C: "Crane",
  F: "Frog",
  G: "Goat",
  M: "Monkey",
  U: "Fungi",
  O: "Octopus",
  P: "Penguin",
  R: "Rhino",
  S: "Snake",
  T: "Tree",
  W: "Wolf",
  V: "Virus",
  // Add more as needed
};

// Debug utility: create a 2D sketch of the board
export function sketchBoard(board: string[][][]): string {
  // board[col][row] (col: 0-4, row: 0-7)
  const numCols = board.length;
  const numRows = board[0].length;
  let lines: string[] = [];
  for (let row = numRows - 1; row >= 0; row--) {
    let line = "";
    for (let col = 0; col < numCols; col++) {
      const pieces = board[col][row];
      if (!pieces || pieces.length === 0) {
        line += "-";
      } else {
        // Show the first character of the top piece (uppercase for light, lowercase for dark)
        const info = getPieceInfo(pieces[0]);
        if (!info.type) {
          line += "-";
        } else {
          line += info.isLight ? info.type : info.type.toLowerCase();
        }
      }
    }
    lines.push(line);
  }
  return lines.join("\n");
}

// Utility to strip the bonus turn suffix from a FEN string
function stripBonusSuffix(fen: string): string {
  return fen.replace(/\~:[a-f0-9\-]+/i, "");
}

/**
 * Detects entanglement that occurred without the Octopus moving.
 * This handles the edge case where an Octopus uses its entangle ability
 * while staying on the same square.
 */
function detectStationaryEntanglement(
  prevBoard: string[][][],
  currBoard: string[][][],
  numCols: number,
  numRows: number
): string | null {
  // Scan for pieces that gained ⛓ modifier
  for (let col = 0; col < numCols; col++) {
    for (let row = 0; row < numRows; row++) {
      const prevSquare = prevBoard[col]?.[row];
      const currSquare = currBoard[col]?.[row];

      if (!prevSquare || !currSquare || currSquare.length === 0) continue;

      // Get the top piece on the square
      const prevPiece = prevSquare.length > 0 ? prevSquare[prevSquare.length - 1] : null;
      const currPiece = currSquare[currSquare.length - 1];

      // Check if this piece gained ⛓ modifier
      const hadEntangle = prevPiece && prevPiece.endsWith("⛓");
      const hasEntangle = currPiece && currPiece.endsWith("⛓");

      if (!hadEntangle && hasEntangle) {
        // Found a piece that was just entangled!
        // Now find the Octopus that did it (search entire board)
        for (let oCol = 0; oCol < numCols; oCol++) {
          for (let oRow = 0; oRow < numRows; oRow++) {
            const octopusSquare = currBoard[oCol]?.[oRow];
            if (!octopusSquare) continue;

            // Look for an Octopus on this square
            const octopusPiece = octopusSquare.find(p => {
              const info = getPieceInfo(p);
              return info.type === "O";
            });

            if (octopusPiece) {
              // Found an Octopus - generate the entanglement description
              const basePiece = currPiece.replace(/⛓+$/, "");
              const entangledInfo = getPieceInfo(basePiece);
              const octopusInfo = getPieceInfo(octopusPiece);

              const actor = getPieceDisplayName("O", octopusInfo.isLight);
              const entangledName = entangledInfo
                ? getPieceDisplayName(entangledInfo.type, entangledInfo.isLight)
                : "piece";
              const entangledSquare = getAlgebraicNotation(col, row);

              return `${actor} entangled ${entangledName} [${entangledSquare}]`;
            }
          }
        }
      }
    }
  }

  return null;
}

export function inferMoveFromFENDelta(
  prevFEN: string,
  currFEN: string,
  wasLightMove?: boolean
): string {
  // Defensive check for empty or malformed FENs
  if (!prevFEN || !currFEN) {
    return "Move could not be inferred (empty FEN)";
  }

  // CRITICAL FIX: Normalize FENs to handle encoding inconsistencies (✳️ vs *)
  prevFEN = normalizeFEN(prevFEN);
  currFEN = normalizeFEN(currFEN);

  // SPECIAL CASE: Empty board to full board (setup completion)
  // If prevFEN is all empty (8/8/8/8/8 or 8/8/8/8/8/8/8/8) and currFEN has pieces, this is setup completion
  const isEmptyBoard = /^[d-]?[8\/]+$/.test(
    prevFEN.replace(/@\d+$/, "").replace(/#\d+$/, "")
  );
  const hasNoPieces = !/[A-Za-z]/.test(prevFEN);
  const hasPieces = /[A-Za-z]/.test(currFEN);

  if (isEmptyBoard && hasNoPieces && hasPieces) {
    return "Game started with initial position";
  }

  // Check for moved annotation: ~{moved:Type,Color,From,To} (board-to-board repositioning)
  const movedMatch = currFEN.match(
    /~\{moved:([^,]+),([^,]+),([^,]+),([^}]+)\}/
  );
  if (movedMatch) {
    const [, pieceType, color, fromPos, toPos] = movedMatch;
    return `Moved ${color} ${pieceType} from [${fromPos}] to [${toPos}]`;
  }

  // Check for placement annotation: ~{placement:Type,Color,Position}
  const placementMatch = currFEN.match(
    /~\{placement:([^,]+),([^,]+),([^}]+)\}/
  );
  if (placementMatch) {
    const [, pieceType, color, position] = placementMatch;

    // Check if the piece was dropped into a tree by looking for T+PieceType pattern in the FEN
    // Extract the FEN portion (before the annotation)
    const fenPortion = currFEN.split("~")[0];

    // Parse the position (e.g., "E4" -> col=4, row=3)
    const col = position.charCodeAt(0) - 65; // 'A' = 0, 'B' = 1, etc.
    const row = parseInt(position.slice(1)) - 1; // Convert 1-indexed to 0-indexed

    try {
      const board = parseFEN(fenPortion);

      // Check if there's a tree at this position
      if (board[col] && board[col][row]) {
        const square = board[col][row];
        const hasTree = square.some((p) => {
          const info = getPieceInfo(p);
          return info.type === "T";
        });

        if (hasTree && square.length > 1) {
          // Find the tree's color
          const treePiece = square.find((p) => getPieceInfo(p).type === "T");
          const treeInfo = treePiece ? getPieceInfo(treePiece) : null;
          const treeColor = treeInfo?.isLight ? "Light" : "Dark";
          return `Dropped ${color} ${pieceType} into ${treeColor} Tree [${position}]`;
        }
      }
    } catch (e) {
      // If parsing fails, fall back to normal placement message
    }

    return `Dropped ${color} ${pieceType} on [${position}]`;
  }

  // Check for bonus turn indicator ("-" prefix)
  const isBonusTurnMove = currFEN.startsWith("-");

  // Helper function to add "Bonus turn" prefix if needed
  const formatDescription = (desc: string): string => {
    if (isBonusTurnMove && !desc.startsWith("Bonus turn")) {
      return "Bonus turn " + desc;
    }
    return desc;
  };

  // Strip bonus turn prefix and suffix for comparison
  let cleanPrevFEN = stripBonusSuffix(prevFEN);
  let cleanCurrFEN = stripBonusSuffix(currFEN);

  // Remove "-" prefix if present
  if (cleanPrevFEN.startsWith("-")) {
    cleanPrevFEN = cleanPrevFEN.slice(1);
  }
  if (cleanCurrFEN.startsWith("-")) {
    cleanCurrFEN = cleanCurrFEN.slice(1);
  }

  // Remove placement/moved annotations if present
  cleanPrevFEN = cleanPrevFEN.replace(/~\{(?:placement|moved):[^}]+\}/, "");
  cleanCurrFEN = cleanCurrFEN.replace(/~\{(?:placement|moved):[^}]+\}/, "");

  let prevBoard: string[][][];
  let currBoard: string[][][];
  try {
    prevBoard = parseFEN(cleanPrevFEN);
    currBoard = parseFEN(cleanCurrFEN.replace(/#(\d+)$/, ""));
  } catch (e) {
    return "Move could not be inferred (FEN parse error)";
  }

  if (
    !Array.isArray(prevBoard) ||
    prevBoard.length === 0 ||
    !Array.isArray(prevBoard[0]) ||
    prevBoard[0].length === 0
  ) {
    return "Move could not be inferred (malformed prevBoard)";
  }
  if (
    !Array.isArray(currBoard) ||
    currBoard.length === 0 ||
    !Array.isArray(currBoard[0]) ||
    currBoard[0].length === 0
  ) {
    return "Move could not be inferred (malformed currBoard)";
  }

  // Defensive: get numCols/numRows from both boards, use the minimum
  const numCols = Math.min(prevBoard.length, currBoard.length);
  const numRows = Math.min(prevBoard[0].length, currBoard[0].length);

  // Extract dice roll from #<number> suffix, if present
  let diceRoll: number | null = null;
  let currFENStripped = cleanCurrFEN;
  const diceMatch = cleanCurrFEN.match(/#(\d+)$/);
  if (diceMatch) {
    diceRoll = parseInt(diceMatch[1], 10);
    currFENStripped = cleanCurrFEN.replace(/#(\d+)$/, "");
  }

  // --- OCTOPUS INK CHECK (hidden information move - creates shadows) ---
  // Ink is detected when: 1 Octopus in prev becomes multiple Octopuses in curr (real + shadows)
  for (const isLight of [true, false]) {
    // Count Octopuses in prevBoard
    let prevOctopusCount = 0;
    let prevOctopusPos: { col: number; row: number } | null = null;
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        const square = prevBoard[col]?.[row];
        if (!square) continue;
        const octopus = square.find((p) => {
          const info = getPieceInfo(p);
          return info.type === "O" && info.isLight === isLight;
        });
        if (octopus) {
          prevOctopusCount++;
          prevOctopusPos = { col, row };
        }
      }
    }

    // Count Octopuses in currBoard
    let currOctopusCount = 0;
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        const square = currBoard[col]?.[row];
        if (!square) continue;
        const octopus = square.find((p) => {
          const info = getPieceInfo(p);
          return info.type === "O" && info.isLight === isLight;
        });
        if (octopus) {
          currOctopusCount++;
        }
      }
    }

    // If we went from 1 Octopus to multiple, it's an ink move
    if (prevOctopusCount === 1 && currOctopusCount > 1 && prevOctopusPos) {
      const actor = getPieceDisplayName("O", isLight);
      const fromSquare = getAlgebraicNotation(prevOctopusPos.col, prevOctopusPos.row);
      return formatDescription(`${actor} used ink on [${fromSquare}]`);
    }
  }

  // --- RHINO TRAMPLE CHECK (detect trample captures along path) ---
  for (const isLight of [true, false]) {
    // Find Rhinos in prevBoard
    const prevRhinos: { col: number; row: number }[] = [];
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        const square = prevBoard[col]?.[row];
        if (!square) continue;
        const rhino = square.find((p) => {
          const info = getPieceInfo(p);
          return info.type === "R" && info.isLight === isLight;
        });
        if (rhino) {
          prevRhinos.push({ col, row });
        }
      }
    }

    // Find Rhinos in currBoard
    const currRhinos: { col: number; row: number }[] = [];
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        const square = currBoard[col]?.[row];
        if (!square) continue;
        const rhino = square.find((p) => {
          const info = getPieceInfo(p);
          return info.type === "R" && info.isLight === isLight;
        });
        if (rhino) {
          currRhinos.push({ col, row });
        }
      }
    }

    // Find Rhino that moved
    for (const prevPos of prevRhinos) {
      const currPos = currRhinos.find(
        (c) => !prevRhinos.some((p) => p.col === c.col && p.row === c.row)
      );

      if (!currPos) continue;

      // Check if this is a straight-line move (trample candidate)
      const isStraightLine =
        prevPos.col === currPos.col || prevPos.row === currPos.row;
      const distance = Math.max(
        Math.abs(currPos.col - prevPos.col),
        Math.abs(currPos.row - prevPos.row)
      );

      // Trample requires straight line and distance > 1
      if (isStraightLine && distance > 1) {
        const colStep =
          prevPos.col === currPos.col ? 0 : currPos.col > prevPos.col ? 1 : -1;
        const rowStep =
          prevPos.row === currPos.row ? 0 : currPos.row > prevPos.row ? 1 : -1;

        const trampled: { type: string; isLight: boolean; pos: string }[] = [];
        let currentCol = prevPos.col + colStep;
        let currentRow = prevPos.row + rowStep;

        // Check all intermediate squares (excluding start and end)
        while (currentCol !== currPos.col || currentRow !== currPos.row) {
          const prevSquare = prevBoard[currentCol]?.[currentRow];
          const currSquare = currBoard[currentCol]?.[currentRow];

          if (prevSquare && prevSquare.length > 0) {
            // Find pieces that were here but aren't anymore
            for (const piece of prevSquare) {
              const info = getPieceInfo(piece);
              const stillHere = currSquare?.some((p) => {
                const currInfo = getPieceInfo(p);
                return (
                  currInfo.type === info.type &&
                  currInfo.isLight === info.isLight
                );
              });

              if (!stillHere && info.type) {
                trampled.push({
                  type: info.type,
                  isLight: info.isLight ?? false,
                  pos: getAlgebraicNotation(currentCol, currentRow),
                });
              }
            }
          }

          currentCol += colStep;
          currentRow += rowStep;
        }

        // Also check destination square for captures
        const destPrevSquare = prevBoard[currPos.col]?.[currPos.row];
        const destCurrSquare = currBoard[currPos.col]?.[currPos.row];
        if (destPrevSquare) {
          for (const piece of destPrevSquare) {
            const info = getPieceInfo(piece);
            // Skip Rhinos (the Rhino moved here)
            if (info.type === "R") continue;

            const stillHere = destCurrSquare?.some((p) => {
              const currInfo = getPieceInfo(p);
              return (
                currInfo.type === info.type && currInfo.isLight === info.isLight
              );
            });

            if (!stillHere && info.type) {
              trampled.push({
                type: info.type,
                isLight: info.isLight ?? false,
                pos: getAlgebraicNotation(currPos.col, currPos.row),
              });
            }
          }
        }

        // If we found trampled pieces, generate the description
        if (trampled.length > 0) {
          const actor = getPieceDisplayName("R", isLight);
          const fromSquare = getAlgebraicNotation(prevPos.col, prevPos.row);
          const toSquare = getAlgebraicNotation(currPos.col, currPos.row);

          // Check zombification status BEFORE generating description
          const prevRhino = prevBoard[prevPos.col]?.[prevPos.row]?.find((p) => {
            const info = getPieceInfo(p);
            return info.type === "R" && info.isLight === isLight;
          });
          const currRhino = currBoard[currPos.col]?.[currPos.row]?.find((p) => {
            const info = getPieceInfo(p);
            return info.type === "R" && info.isLight === isLight;
          });

          const zombieMarkers = ["z"];
          const wasZombified =
            prevRhino && zombieMarkers.some((m) => prevRhino.includes(m));

          const trampledNames = trampled.map(
            (t) => `${getPieceDisplayName(t.type, t.isLight)} [${t.pos}]`
          );
          const trampledStr =
            trampledNames.length > 1
              ? trampledNames.slice(0, -1).join(", ") +
                " and " +
                trampledNames.slice(-1)
              : trampledNames[0];

          // Prepend "Zombified" if the Rhino was already zombified before this move
          let description = wasZombified
            ? `Zombified ${actor} trampled ${trampledStr} [${fromSquare} -> ${toSquare}]`
            : `${actor} trampled ${trampledStr} [${fromSquare} -> ${toSquare}]`;

          // Check if Rhino got poisoned (trampled a Frog)
          const poisonMarkers = ["!", "\u2620", "\u2620\uFE0F", "☠", "☣"];
          const wasPoisoned =
            prevRhino && poisonMarkers.some((m) => prevRhino.includes(m));
          const isPoisoned =
            currRhino && poisonMarkers.some((m) => currRhino.includes(m));

          if (!wasPoisoned && isPoisoned) {
            description += ` The ${actor} is now poisoned`;
          }

          // Check if Rhino got zombified (trampled a Fungi)
          const isZombified =
            currRhino && zombieMarkers.some((m) => currRhino.includes(m));

          if (!wasZombified && isZombified) {
            description += ` ${actor} is now zombified`;
          }

          // Check if Rhino got infected (trampled a virus-infected piece)
          const wasInfected = prevRhino && (prevRhino.includes("+v") || prevRhino.includes("+V"));
          const isInfected = currRhino && (currRhino.includes("+v") || currRhino.includes("+V"));

          if (!wasInfected && isInfected) {
            description += ` ${actor} is now infected`;
          }

          return formatDescription(description);
        }
      }
    }
  }

  // --- RHINO LANE SWAP CHECK (before move candidate logic) ---
  for (const isLight of [true, false]) {
    const homeRank = isLight ? 0 : numRows - 1;
    for (let colA = 0; colA < numCols; colA++) {
      for (let colB = 0; colB < numCols; colB++) {
        if (colA === colB) continue;
        // Defensive checks for all accesses
        if (
          !prevBoard[colA] ||
          !prevBoard[colA][homeRank] ||
          !prevBoard[colB] ||
          !prevBoard[colB][homeRank] ||
          !currBoard[colA] ||
          !currBoard[colA][homeRank] ||
          !currBoard[colB] ||
          !currBoard[colB][homeRank]
        ) {
          continue;
        }
        const prevA = prevBoard[colA][homeRank][0];
        const prevB = prevBoard[colB][homeRank][0];
        const currA = currBoard[colA][homeRank][0];
        const currB = currBoard[colB][homeRank][0];
        if (
          prevA &&
          prevB &&
          currA &&
          currB &&
          getPieceInfo(prevA).type === "R" &&
          getPieceInfo(prevA).isLight === isLight &&
          getPieceInfo(prevB).isLight === isLight &&
          getPieceInfo(prevB).type !== "R" &&
          getPieceInfo(currA).isLight === isLight &&
          getPieceInfo(currA).type !== "R" &&
          getPieceInfo(currB).type === "R" &&
          getPieceInfo(currB).isLight === isLight &&
          prevA !== currA &&
          prevB !== currB &&
          // Ensure the swap is mutual
          getPieceInfo(currA).type === getPieceInfo(prevB).type &&
          getPieceInfo(currB).type === getPieceInfo(prevA).type
        ) {
          const actor = getPieceDisplayName("R", isLight);
          const swapped = getPieceDisplayName(
            getPieceInfo(prevB).type,
            isLight
          );
          const fromSquare = getAlgebraicNotation(colA, homeRank);
          const toSquare = getAlgebraicNotation(colB, homeRank);
          return formatDescription(
            `${actor} used Lane to swap places with ${swapped} [${fromSquare} -> ${toSquare}]`
          );
        }
      }
    }
  }

  // --- SNAKE RESPAWN CHECK (run before main move/capture logic) ---
  for (let col = 0; col < numCols; col++) {
    for (let row = 0; row < numRows; row++) {
      if (
        !prevBoard[col] ||
        !prevBoard[col][row] ||
        !currBoard[col] ||
        !currBoard[col][row]
      )
        continue;
      const prevSnake = prevBoard[col][row].find(
        (p) => getPieceInfo(p).type === "S" && p.includes("*")
      );
      if (prevSnake) {
        // In currFEN, that square contains a non-Snake piece (the captor)
        const currNonSnake = currBoard[col][row].find(
          (p) => getPieceInfo(p).type !== "S"
        );
        if (currNonSnake) {
          const prevSnakeInfo = getPieceInfo(prevSnake);
          const respawnRow = prevSnakeInfo.isLight ? 0 : numRows - 1;
          if (!currBoard[col] || !currBoard[col][respawnRow]) continue;
          // In currFEN, a Snake of the same color without '*' appears in the respawn row of the same column
          const currSnake = currBoard[col][respawnRow].find((p) => {
            const info = getPieceInfo(p);
            return (
              info.type === "S" &&
              info.isLight === prevSnakeInfo.isLight &&
              !p.includes("*")
            );
          });
          if (currSnake) {
            const captorInfo = getPieceInfo(currNonSnake);
            const actor = getPieceDisplayName(
              captorInfo.type,
              captorInfo.isLight
            );
            const captured = getPieceDisplayName("S", prevSnakeInfo.isLight);
            // Find the captor's previous location
            let fromSquare = getAlgebraicNotation(col, row); // fallback
            for (let cCol = 0; cCol < numCols; cCol++) {
              for (let cRow = 0; cRow < numRows; cRow++) {
                if (!prevBoard[cCol] || !prevBoard[cCol][cRow]) continue;
                if (
                  (cCol !== col || cRow !== row) &&
                  prevBoard[cCol][cRow].some((p) => {
                    const info = getPieceInfo(p);
                    return (
                      info.type === captorInfo.type &&
                      info.isLight === captorInfo.isLight
                    );
                  })
                ) {
                  fromSquare = getAlgebraicNotation(cCol, cRow);
                }
              }
            }
            const toSquare = getAlgebraicNotation(col, row);
            const respawnSquare = getAlgebraicNotation(col, respawnRow);
            return formatDescription(
              `${actor} captured ${captured} [${fromSquare} -> ${toSquare}] ${captured} respawned [${respawnSquare}]`
            );
          }
        }
      }
    }
  }

  let from: [number, number] | null = null;
  let to: [number, number] | null = null;
  let piece: { type: string; isLight: boolean | null; raw: string } | null =
    null;
  let captured: { type: string; isLight: boolean | null; raw?: string } | null = null;
  let specialEffect: string | null = null;

  // 1. Find all pieces in prev and curr, indexed by [col,row] and piece type/color (ignore modifiers for matching)
  type PieceInfo = {
    raw: string;
    type: string;
    isLight: boolean;
    isZombified: boolean;
    col: number;
    row: number;
  };
  const prevPieces: PieceInfo[] = [];
  const currPieces: PieceInfo[] = [];

  for (let col = 0; col < numCols; col++) {
    if (!prevBoard[col] || !currBoard[col]) {
      continue;
    }
    for (let row = 0; row < numRows; row++) {
      if (!prevBoard[col][row] || !currBoard[col][row]) {
        continue;
      }
      // Ignore ⛓ for move detection
      for (const p of prevBoard[col][row]) {
        if (p === "⛓") continue;
        const info = getPieceInfo(p);
        prevPieces.push({
          raw: p,
          type: info.type,
          isLight: info.isLight ?? false,
          isZombified: info.isZombified,
          col,
          row,
        });
      }
      for (const c of currBoard[col][row]) {
        if (c === "⛓") continue;
        const info = getPieceInfo(c);
        currPieces.push({
          raw: c,
          type: info.type,
          isLight: info.isLight ?? false,
          isZombified: info.isZombified,
          col,
          row,
        });
      }
    }
  }

  // DEBUG: Log collected pieces

  // 2. Find the piece that moved: present in curr, not in prev at that square, and not just a modifier change
  let moveCandidate: PieceInfo | null = null;
  let moveFrom: [number, number] | null = null;
  let moveTo: [number, number] | null = null;
  let prevRaw: string | null = null;
  let currRaw: string | null = null;
  let prevOriginIsLight: boolean | null = null; // Track original piece color for zombified pieces

  // Try to match by type/color and position, but allow for modifier change
  for (const curr of currPieces) {
    // If wasLightMove is not provided, infer from FEN turn indicator
    let forceIsLight: boolean | undefined = undefined;
    if (typeof wasLightMove !== "boolean") {
      // If the FEN does not start with 'd', it's always a light move
      if (!currFEN.trim().startsWith("d")) {
        forceIsLight = true;
      } else {
        forceIsLight = false;
      }
    }
    // Find a prev piece of same type/color at same square
    const prevMatch = prevPieces.find(
      (p) =>
        p.type === curr.type &&
        p.isLight === curr.isLight &&
        p.col === curr.col &&
        p.row === curr.row
    );
    if (!prevMatch) {
      // This piece appeared here (move destination)
      // BUT: if we know who moved (wasLightMove), skip pieces of the wrong color
      // This handles cases like Snake respawn where an opponent's piece appears
      //
      // EXCEPTION: Zombified pieces are controlled by the OPPOSITE player
      // But we only care about zombification BEFORE the move, not AFTER
      // - If piece was zombified BEFORE → controlled by opposite player
      // - If piece BECOMES zombified NOW → still controlled by original player (moved then got zombified)

      // Check if there's a zombified version of this piece in prevPieces (was zombified before)
      const prevZombified = prevPieces.find(
        p => p.type === curr.type && p.isLight === curr.isLight && p.isZombified &&
             (p.col !== curr.col || p.row !== curr.row)
      );

      // Determine who controls this piece based on PRE-MOVE state
      // If it was zombified before, it's controlled by the opposite player
      // If it wasn't zombified before (even if it is now), it's controlled by the original player
      const wasZombifiedBefore = !!prevZombified;
      const isControlledByLight = wasZombifiedBefore ? !curr.isLight : curr.isLight;

      if (typeof wasLightMove === "boolean" && isControlledByLight !== wasLightMove) {
        continue; // Skip this piece, it's not the one that moved
      }

      moveCandidate = curr;
      moveTo = [curr.col, curr.row];

      // Try to find where it came from (same type/color, different square)
      let prevOrigin: PieceInfo | undefined;
      if (typeof wasLightMove === "boolean") {
        // Account for zombified pieces: they act as opposite color but retain original isLight
        // First try exact match with wasLightMove
        prevOrigin = prevPieces.find(
          (p) =>
            p.type === curr.type &&
            p.isLight === wasLightMove &&
            (p.col !== curr.col || p.row !== curr.row)
        );

        // If not found, try opposite color (for zombified pieces)
        if (!prevOrigin) {
          prevOrigin = prevPieces.find(
            (p) =>
              p.type === curr.type &&
              p.isLight === !wasLightMove &&
              p.isZombified &&  // Must be zombified to act as opposite color
              (p.col !== curr.col || p.row !== curr.row)
          );
        }
      } else {
        // When wasLightMove is not provided, find the origin based on the piece that moved
        // First try with the same color as the current piece
        prevOrigin = prevPieces.find(
          (p) =>
            p.type === curr.type &&
            p.isLight === curr.isLight &&
            (p.col !== curr.col || p.row !== curr.row)
        );

        // If not found and forceIsLight is defined, try with that color constraint
        if (!prevOrigin && typeof forceIsLight === "boolean") {
          prevOrigin = prevPieces.find(
            (p) =>
              p.type === curr.type &&
              p.isLight === forceIsLight &&
              (p.col !== curr.col || p.row !== curr.row)
          );
        }
      }
      if (prevOrigin) {
        moveFrom = [prevOrigin.col, prevOrigin.row];
        prevRaw = prevOrigin.raw;
        currRaw = curr.raw;
        prevOriginIsLight = prevOrigin.isLight; // Save for zombified piece description
      } else if (typeof wasLightMove === "boolean") {
        // Special-case: If this is the final move and we can't find the FROM, try to find the only matching piece in prev that is now missing in curr
        // Account for zombified pieces acting as opposite color
        const prevCandidates = prevPieces.filter(
          (p) => p.type === curr.type && (p.isLight === wasLightMove || (p.isZombified && p.isLight === !wasLightMove))
        );
        const currCandidates = currPieces.filter(
          (p) => p.type === curr.type && (p.isLight === wasLightMove || (p.isZombified && p.isLight === !wasLightMove))
        );
        if (prevCandidates.length === 1 && currCandidates.length === 1) {
          // The piece moved from prevCandidates[0] to currCandidates[0]
          moveFrom = [prevCandidates[0].col, prevCandidates[0].row];
          prevRaw = prevCandidates[0].raw;
          currRaw = currCandidates[0].raw;
        } else if (prevCandidates.length > 0) {
          // Find the prev candidate that is not present in curr
          const missing = prevCandidates.find(
            (p) =>
              !currCandidates.some((c) => c.col === p.col && c.row === p.row)
          );
          if (missing) {
            moveFrom = [missing.col, missing.row];
            prevRaw = missing.raw;
            currRaw = curr.raw;
          }
        }
      }
      break;
    }
  }

  // DEBUG: Log move candidate detection result

  // 3. Find the captured piece: present in prev at destination, not in curr at destination, and not the moving piece
  if (moveTo) {
    const [toCol, toRow] = moveTo;
    const prevAtDestRaw = prevBoard[toCol][toRow]; // Keep raw strings
    const prevAtDest = prevAtDestRaw.map(getPieceInfo);
    const currAtDest = currBoard[toCol][toRow].map(getPieceInfo);

    // Find a prev piece at dest that is not present in curr (by type/color)
    for (let i = 0; i < prevAtDest.length; i++) {
      const prevPiece = prevAtDest[i];
      const prevPieceRaw = prevAtDestRaw[i];
      if (
        !currAtDest.some(
          (c) => c.type === prevPiece.type && c.isLight === prevPiece.isLight
        ) &&
        // Not the moving piece
        !(
          moveCandidate &&
          prevPiece.type === moveCandidate.type &&
          (prevPiece.isLight ?? false) === (moveCandidate.isLight ?? false)
        )
      ) {
        captured = {
          type: prevPiece.type,
          isLight: prevPiece.isLight ?? false,
          raw: prevPieceRaw, // Include raw string for status marker detection
        };
        break;
      }
    }
  }

  // 4. Detect special effect: if the moving piece gained a '!' (poison) or other modifier
  if (moveFrom && moveTo && prevRaw && currRaw) {
    // Poison: gained '!' or skull/biohazard unicode modifier
    const poisonMarkers = ["!", "\u2620", "\u2620\uFE0F", "☠", "☣"];
    const prevIsPoisoned = poisonMarkers.some(
      (m) => prevRaw?.includes(m) ?? false
    );
    const currIsPoisoned = poisonMarkers.some(
      (m) => currRaw?.includes(m) ?? false
    );

    if (!prevIsPoisoned && currIsPoisoned) {
      specialEffect = "poisoned";
    }
    // Zombified: gained 'z' modifier
    if (!prevRaw.includes("z") && currRaw.includes("z")) {
      specialEffect = "zombified";
    }
  }

  // Zombification status for the moving piece
  const prevIsZombified = prevRaw && getPieceInfo(prevRaw).isZombified;
  const currIsZombified = currRaw && getPieceInfo(currRaw).isZombified;


  // 5. Compose the move description
  if (moveFrom && moveTo && moveCandidate) {
    // --- RHINO LANE ABILITY CHECK (robust, any two home-rank squares) ---
    if (
      moveCandidate.type === "R" &&
      moveFrom &&
      moveTo &&
      moveFrom[1] === moveTo[1] // same row
    ) {
      const rhinoIsLight = moveCandidate.isLight;
      const homeRank = rhinoIsLight ? 0 : numRows - 1;
      if (moveFrom[1] === homeRank && moveTo[1] === homeRank) {
        // The piece now at the Rhino's original square
        const nowAtFrom = currBoard[moveFrom[0]][moveFrom[1]][0];
        const prevAtDest = prevBoard[moveTo[0]][moveTo[1]][0];
        const prevAtFrom = prevBoard[moveFrom[0]][moveFrom[1]][0];
        const nowAtDest = currBoard[moveTo[0]][moveTo[1]][0];
        // Check that the Rhino moved to the destination
        const rhinoAtDest =
          nowAtDest &&
          getPieceInfo(nowAtDest).type === "R" &&
          getPieceInfo(nowAtDest).isLight === rhinoIsLight;
        // Check that the swapped piece is same color, not a Rhino, and was at the destination before
        const swappedInfo = nowAtFrom && getPieceInfo(nowAtFrom);
        const wasAtDest =
          prevAtDest && swappedInfo && prevAtDest[0] === swappedInfo.type[0];
        if (
          rhinoAtDest &&
          swappedInfo &&
          swappedInfo.isLight === rhinoIsLight &&
          swappedInfo.type !== "R" &&
          prevAtDest &&
          getPieceInfo(prevAtDest).type === swappedInfo.type &&
          getPieceInfo(prevAtDest).isLight === swappedInfo.isLight &&
          prevAtFrom &&
          getPieceInfo(prevAtFrom).type === "R" &&
          getPieceInfo(prevAtFrom).isLight === rhinoIsLight
        ) {
          const actor = getPieceDisplayName("R", rhinoIsLight);
          const swapped = getPieceDisplayName(
            swappedInfo.type,
            swappedInfo.isLight
          );
          const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
          const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
          return formatDescription(
            `${actor} used Lane to swap places with ${swapped} [${fromSquare} -> ${toSquare}]`
          );
        }
      }
    }
    let moveType = "move";
    if (captured) moveType = "capture";
    // Special case: Tree crushing a fainted Goat
    if (
      moveCandidate.type === "T" &&
      captured &&
      captured.type === "G" &&
      captured.raw &&
      captured.raw.includes("$")
    ) {
      moveType = "crush";
    }
    // Special case: Frog lilyjump (lost its * or ✳️)
    const isLilyjump =
      moveCandidate.type === "F" &&
      prevRaw &&
      currRaw &&
      (prevRaw.includes("*") || prevRaw.includes("✳️")) &&
      !(currRaw.includes("*") || currRaw.includes("✳️"));

    if (isLilyjump) {
      // If there's also a capture, combine both aspects
      moveType = captured ? "lilyjump-capture" : "lilyjump";
    }
    // Special case: Goat traverse (move more than 1 square)
    if (
      moveCandidate.type === "G" &&
      (Math.abs(moveFrom[0] - moveTo[0]) > 1 ||
        Math.abs(moveFrom[1] - moveTo[1]) > 1)
    ) {
      moveType = "traverse";
    }
    // Special case: Monkey leap (move more than 1 square)
    let leapedOverPieces: { type: string; isLight: boolean }[] = [];
    let leapedOverTree = false;
    if (
      moveCandidate.type === "M" &&
      (Math.abs(moveFrom[0] - moveTo[0]) > 1 ||
        Math.abs(moveFrom[1] - moveTo[1]) > 1)
    ) {
      moveType = "leap";
      // Collect all pieces leaped over along the path
      const dCol = Math.sign(moveTo[0] - moveFrom[0]);
      const dRow = Math.sign(moveTo[1] - moveFrom[1]);

      // Calculate the number of steps needed (max of col/row distances)
      const colSteps = Math.abs(moveTo[0] - moveFrom[0]);
      const rowSteps = Math.abs(moveTo[1] - moveFrom[1]);
      const maxSteps = Math.max(colSteps, rowSteps);

      for (let step = 1; step < maxSteps; step++) {
        // Calculate position for this step
        const stepCol = moveFrom[0] + dCol * step;
        const stepRow = moveFrom[1] + dRow * step;

        const square = prevBoard[stepCol]?.[stepRow] || [];
        if (square.length > 0) {
          const info = getPieceInfo(square[0]);
          leapedOverPieces.push({
            type: info.type,
            isLight: info.isLight ?? false,
          });
          if (info.type === "T") {
            leapedOverTree = true;
          }
        }
      }
    }
    // Special case: Monkey consumes Fungi (any color)
    if (moveCandidate.type === "M" && captured && captured.type === "U") {
      moveType = "consume";
      const actor = getPieceDisplayName(moveCandidate.type, moveCandidate.isLight);
      const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
      const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
      const capturedName = getPieceDisplayName(captured.type, captured.isLight);

      let desc;
      // Check if monkey leaped over pieces to reach the fungi (leap-consume combo)
      if (leapedOverPieces.length > 0) {
        const leapedNames = leapedOverPieces.map((p) =>
          getPieceDisplayName(p.type, p.isLight)
        );
        let leapedStr = leapedNames.join(", ");
        if (leapedNames.length > 1) {
          leapedStr = leapedNames.slice(0, -1).join(", ") + " and " + leapedNames.slice(-1);
        }
        desc = `${actor} leaped over ${leapedStr} and consumed ${capturedName} [${fromSquare} -> ${toSquare}] ${actor} gets a bonus turn`;
      } else {
        // Regular 1-square consume (no leap)
        desc = generateMoveDescription({
          pieceType: moveCandidate.type,
          isLight: moveCandidate.isLight,
          from: moveFrom,
          to: moveTo,
          moveType,
          captured,
          specialEffect: null,
        });
      }

      if (diceRoll !== null) {
        desc += ` ${actor} rolled a ${diceRoll}`;
      }
      return desc;
    }
    // Special case: Crane flew into Tree
    let flewTreeType = null;
    if (
      moveCandidate.type === "C" &&
      currBoard[moveTo[0]][moveTo[1]].some((p) => {
        const info = getPieceInfo(p);
        return info.type === "T";
      })
    ) {
      // Find the tree in the destination square
      const treePiece = currBoard[moveTo[0]][moveTo[1]].find(
        (p) => getPieceInfo(p).type === "T"
      );
      if (treePiece) {
        const treeInfo = getPieceInfo(treePiece);
        flewTreeType = treeInfo.isLight ? "Light Tree" : "Dark Tree";
      } else {
        flewTreeType = "Tree";
      }
      // Custom move description
      const actor = getPieceDisplayName(
        moveCandidate.type,
        moveCandidate.isLight
      );
      const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
      const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
      let desc = `${actor} flew into ${flewTreeType} [${fromSquare} -> ${toSquare}]`;
      if (diceRoll !== null) {
        desc += ` ${actor} rolled a ${diceRoll}`;
      }
      return formatDescription(desc);
    }
    // Special case: Tree walk with housing (Tree moves and destination contains both a Tree and another piece of the same color)
    if (moveCandidate && moveFrom && moveTo && moveCandidate.type === "T") {
      const candidateIsLight = moveCandidate.isLight;
      // Find another piece (not a Tree) of the same color in the destination
      const housed = currBoard[moveTo[0]][moveTo[1]].find((p) => {
        const info = getPieceInfo(p);
        return info.type !== "T" && info.isLight === candidateIsLight;
      });
      if (housed) {
        const actor = getPieceDisplayName(
          moveCandidate.type,
          moveCandidate.isLight
        );
        const housedPiece = getPieceDisplayName(
          getPieceInfo(housed).type,
          moveCandidate.isLight
        );
        const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
        const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
        return formatDescription(
          `${actor} housing ${housedPiece} moved [${fromSquare} -> ${toSquare}]`
        );
      }
    }
    // Special case: Tree climb (move onto a Tree of the same color, but not housing another piece)
    if (moveCandidate && moveFrom && moveTo && moveCandidate.type !== "T") {
      const candidateType = moveCandidate.type;
      const candidateIsLight = moveCandidate.isLight;
      const currStack = currBoard[moveTo[0]][moveTo[1]];
      const prevStack = prevBoard[moveTo[0]][moveTo[1]];
      const hasSameColorTreeNow = currStack.some((p) => {
        const info = getPieceInfo(p);
        return info.type === "T" && info.isLight === candidateIsLight;
      });
      const hasCandidateNow = currStack.some((p) => {
        const info = getPieceInfo(p);
        return info.type === candidateType && info.isLight === candidateIsLight;
      });
      const hadSameColorTreeBefore = prevStack.some((p) => {
        const info = getPieceInfo(p);
        return info.type === "T" && info.isLight === candidateIsLight;
      });
      const hadCandidateBefore = prevStack.some((p) => {
        const info = getPieceInfo(p);
        return info.type === candidateType && info.isLight === candidateIsLight;
      });
      // Only trigger if the stack now has both, but did not before
      if (
        hasSameColorTreeNow &&
        hasCandidateNow &&
        !(hadSameColorTreeBefore && hadCandidateBefore)
      ) {
        const actor = getPieceDisplayName(
          moveCandidate.type,
          moveCandidate.isLight
        );
        const tree = getPieceDisplayName("T", moveCandidate.isLight);
        const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
        const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
        return formatDescription(
          `${actor} climbed ${tree} [${fromSquare} -> ${toSquare}]`
        );
      }
    }
    if (moveType === "leap" && leapedOverPieces.length > 0) {
      const actor = getPieceDisplayName(
        moveCandidate.type,
        moveCandidate.isLight
      );
      const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
      const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
      const leapedNames = leapedOverPieces.map((p) =>
        getPieceDisplayName(p.type, p.isLight)
      );
      let leapedStr = leapedNames.join(", ");
      if (leapedNames.length > 1) {
        // Use Oxford comma and 'and' for the last item
        leapedStr =
          leapedNames.slice(0, -1).join(", ") + " and " + leapedNames.slice(-1);
      }
      let desc;
      if (leapedOverTree) {
        desc = `${actor} swung over ${leapedStr} [${fromSquare} -> ${toSquare}] ${actor} gets a bonus turn`;
      } else {
        desc = `${actor} leaped over ${leapedStr} [${fromSquare} -> ${toSquare}]`;
      }
      if (diceRoll !== null) {
        desc += ` ${actor} rolled a ${diceRoll}`;
      }
      return formatDescription(desc);
    }
    // Special case: Virus infects and/or captures
    if (moveCandidate && moveCandidate.type === "V" && moveFrom && moveTo) {
      // Check if the destination square now contains a newly infected piece (now stacked with a Virus, but wasn't before)
      const prevAtDest = prevBoard[moveTo[0]][moveTo[1]];
      const currAtDest = currBoard[moveTo[0]][moveTo[1]];
      let infectedPiece: { type: string; isLight: boolean } | null = null;

      // DEBUG: Log virus infection detection

      // Look for a non-Virus piece now stacked with a Virus that wasn't before
      for (const currPiece of currAtDest) {
        const currInfo = getPieceInfo(currPiece);
        if (currInfo.type === "V") {
          continue; // skip the Virus itself
        }
        // Is this piece now stacked with a Virus?
        const isNowInfected = currAtDest.some(
          (p) => getPieceInfo(p).type === "V"
        );
        // Was it previously stacked with a Virus?
        const wasPreviouslyInfected =
          prevAtDest.some(
            (p) =>
              getPieceInfo(p).type === currInfo.type &&
              getPieceInfo(p).isLight === currInfo.isLight
          ) && prevAtDest.some((p) => getPieceInfo(p).type === "V");
        if (isNowInfected && !wasPreviouslyInfected) {
          infectedPiece = { type: currInfo.type, isLight: !!currInfo.isLight };
          break;
        }
      }
      // Check if the Virus left behind and captured a piece (e.g., a Snake)
      let capturedPiece: { type: string; isLight: boolean } | null = null;
      const prevAtFrom = prevBoard[moveFrom[0]][moveFrom[1]];
      const currAtFrom = currBoard[moveFrom[0]][moveFrom[1]];
      for (const prevPiece of prevAtFrom) {
        const info = getPieceInfo(prevPiece);
        if (
          info.type !== "V" &&
          !currAtFrom.some(
            (c) =>
              getPieceInfo(c).type === info.type &&
              getPieceInfo(c).isLight === !!info.isLight
          )
        ) {
          capturedPiece = { type: info.type, isLight: !!info.isLight };
          break;
        }
      }
      // Compose the move description
      // For zombified pieces, we want to show "Zombified Light Bear" not "Dark Bear"
      // even though the piece is being controlled by the Dark player
      // Use prevOriginIsLight if available (for zombified pieces), otherwise use moveCandidate.isLight
      const pieceIsLight = prevIsZombified && prevOriginIsLight !== null
        ? prevOriginIsLight
        : moveCandidate.isLight;
      const actor = getPieceDisplayName(
        moveCandidate.type,
        pieceIsLight
      );
      const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
      const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
      let desc = "";

      // Zombification: Prepend "Zombified" if piece was already zombified before this move
      // (Don't prepend if it just became zombified this turn - that's handled later)
      if (prevIsZombified) {
        desc += "Zombified ";
      }
      // Check if moving onto a fainted Goat (multiple pieces in square and one is a fainted Goat)
      const currAtTo = currBoard[moveTo[0]][moveTo[1]];
      const hasFaintedGoat = currAtTo.some(p => {
        const info = getPieceInfo(p);
        return info.type === "G" && p.includes("$");
      });
      // Piece stacking is detected by multiple pieces in the same square (not by + symbol, which is stripped during parsing)
      const isStacked = currAtTo.length > 1;
      const standingOnGoat = isStacked && hasFaintedGoat;

      if (infectedPiece) {
        desc += `${actor} infected ${getPieceDisplayName(
          infectedPiece.type,
          infectedPiece.isLight
        )} [${fromSquare} -> ${toSquare}]`;
      } else if (standingOnGoat) {
        // Find the fainted Goat to get its color
        const goatPiece = currAtTo.find(p => {
          const info = getPieceInfo(p);
          return info.type === "G" && p.includes("$");
        });
        const goatInfo = goatPiece ? getPieceInfo(goatPiece) : null;
        const goatName = goatInfo ? getPieceDisplayName("G", goatInfo.isLight) : "Goat";
        desc += `${actor} stands on fainted ${goatName} [${fromSquare} -> ${toSquare}]`;
      } else {
        desc += `${actor} moved [${fromSquare} -> ${toSquare}]`;
      }
      if (capturedPiece) {
        desc += ` ${actor} captured ${getPieceDisplayName(
          capturedPiece.type,
          capturedPiece.isLight
        )} [${fromSquare}]`;
      }
      // Zombification status change descriptions
      if (!prevIsZombified && currIsZombified) {
        // Piece just became zombified
        if (!desc.toLowerCase().includes("is now zombified")) {
          desc += ` ${pieceIsLight ? "Light" : "Dark"} ${
            pieceTypeMap[moveCandidate.type] || moveCandidate.type
          } is now zombified`;
        }
      }
      // Note: If piece was already zombified (prevIsZombified), we already prepended "Zombified" above
      // If zombification wore off (prevIsZombified && !currIsZombified), we also already prepended "Zombified" above
      return formatDescription(desc);
    }
    // Special case: Octopus entanglement (piece gains ⛓ effect in currBoard vs prevBoard)
    if (moveCandidate && moveCandidate.type === "O" && moveFrom && moveTo) {
      for (let col = 0; col < numCols; col++) {
        for (let row = 0; row < numRows; row++) {
          const prevStack = prevBoard[col][row];
          const currStack = currBoard[col][row];
          // Only check if both stacks are non-empty
          if (currStack.length > 0) {
            const currPiece = currStack[currStack.length - 1];
            const prevPiece =
              prevStack.length > 0 ? prevStack[prevStack.length - 1] : null;
            if (
              currPiece.endsWith("⛓") &&
              (!prevPiece || !prevPiece.endsWith("⛓"))
            ) {
              // Entangled piece is the base piece (without ⛓)
              const basePiece = currPiece.replace(/⛓+$/, "");
              const entangledInfo = getPieceInfo(basePiece);
              const actor = getPieceDisplayName("O", moveCandidate.isLight);
              const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
              const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
              const entangledSquare = getAlgebraicNotation(col, row);
              const entangledName = entangledInfo
                ? getPieceDisplayName(entangledInfo.type, entangledInfo.isLight)
                : "piece";
              return formatDescription(
                `${actor} moved [${fromSquare} -> ${toSquare}] and Entangled ${entangledName} [${entangledSquare}]`
              );
            }
          }
        }
      }
    }
    // When calling generateMoveDescription, use wasLightMove if provided
    // EXCEPT for zombified pieces - use their original color
    let isLight = moveCandidate.isLight;
    if (typeof wasLightMove === "boolean" && !prevIsZombified) {
      isLight = wasLightMove;
    } else if (prevIsZombified && prevOriginIsLight !== null) {
      // For zombified pieces, use the original piece color
      isLight = prevOriginIsLight;
    }
    let desc = generateMoveDescription({
      pieceType: moveCandidate.type,
      isLight,
      from: moveFrom,
      to: moveTo,
      moveType,
      captured,
      specialEffect,
    });

    // Check if piece stands on a fainted Goat
    if (moveFrom && moveTo) {
      const currAtTo = currBoard[moveTo[0]][moveTo[1]];
      const hasFaintedGoat = currAtTo.some(p => {
        const info = getPieceInfo(p);
        return info.type === "G" && p.includes("$");
      });
      // Piece stacking is detected by multiple pieces in the same square (not by + symbol, which is stripped during parsing)
      const isStacked = currAtTo.length > 1;
      const standingOnGoat = isStacked && hasFaintedGoat;

      if (standingOnGoat && moveType !== "capture") {
        // Find the fainted Goat to get its color
        const goatPiece = currAtTo.find(p => {
          const info = getPieceInfo(p);
          return info.type === "G" && p.includes("$");
        });
        const goatInfo = goatPiece ? getPieceInfo(goatPiece) : null;
        const goatName = goatInfo ? getPieceDisplayName("G", goatInfo.isLight) : "Goat";
        const actor = getPieceDisplayName(moveCandidate.type, moveCandidate.isLight);
        const fromSquare = getAlgebraicNotation(moveFrom[0], moveFrom[1]);
        const toSquare = getAlgebraicNotation(moveTo[0], moveTo[1]);
        desc = `${actor} stands on fainted ${goatName} [${fromSquare} -> ${toSquare}]`;
      }
    }

    if (diceRoll !== null) {
      desc += ` ${getPieceDisplayName(
        moveCandidate.type,
        moveCandidate.isLight
      )} rolled a ${diceRoll}`;
    }
    // Swing bonus turn description is now handled inline in the leap special case above
    // This section is for non-special-case leap moves that happen to swing
    if (moveType === "leap" && leapedOverTree && !desc.includes("swung over")) {
      desc += ` ${getPieceDisplayName(
        moveCandidate.type,
        moveCandidate.isLight
      )} gets a bonus turn`;
    }
    // Zombification move description logic
    if (!prevIsZombified && currIsZombified) {
      // Piece just became zombified this move
      if (!desc.toLowerCase().includes("is now zombified")) {
        desc += ` ${isLight ? "Light" : "Dark"} ${
          pieceTypeMap[moveCandidate.type] || moveCandidate.type
        } is now zombified`;
      }
    } else if (prevIsZombified && currIsZombified) {
      // Piece was zombified and still is
      if (!desc.startsWith("Zombified")) {
        desc = `Zombified ${desc.charAt(0).toUpperCase()}${desc.slice(1)}`;
      }
    } else if (prevIsZombified && !currIsZombified) {
      // Piece was zombified but zombification just expired
      // Still prepend "Zombified" because the move happened while zombified
      if (!desc.startsWith("Zombified")) {
        desc = `Zombified ${desc.charAt(0).toUpperCase()}${desc.slice(1)}`;
      }
    }

    // Infection status logic - check if piece became infected (captured a virus-infected piece)
    if (moveFrom && moveTo && prevRaw && currRaw) {
      // Check if there's a virus in the same square (pieces are stored separately in the board array)
      const prevSquare = prevBoard[moveFrom[0]]?.[moveFrom[1]] || [];
      const currSquare = currBoard[moveTo[0]]?.[moveTo[1]] || [];

      const wasInfected = prevRaw.includes("+v") || prevRaw.includes("+V") ||
                         prevSquare.some(p => getPieceInfo(p).type === "V");
      const isInfected = currRaw.includes("+v") || currRaw.includes("+V") ||
                        currSquare.some(p => getPieceInfo(p).type === "V");

      if (!wasInfected && isInfected) {
        // Piece just became infected this move
        if (!desc.toLowerCase().includes("is now infected")) {
          desc += ` ${isLight ? "Light" : "Dark"} ${
            pieceTypeMap[moveCandidate.type] || moveCandidate.type
          } is now infected`;
        }
      }
    }

    // Check for meadow effect changes on Trees (Fungi move near/away from enemy Tree)
    const meadowMarkers = ["⚘", "#"];
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        const prevSquare = prevBoard[col]?.[row];
        const currSquare = currBoard[col]?.[row];
        if (!prevSquare || !currSquare) continue;

        // Look for Trees that gained or lost meadow marker
        for (const currPiece of currSquare) {
          const currInfo = getPieceInfo(currPiece);
          if (currInfo.type !== "T") continue;

          const wasMeadowed = prevSquare.some((p) => {
            const info = getPieceInfo(p);
            return info.type === "T" && info.isLight === currInfo.isLight && meadowMarkers.some((m) => p.includes(m));
          });
          const isMeadowed = meadowMarkers.some((m) => currPiece.includes(m));

          if (!wasMeadowed && isMeadowed) {
            const treeName = getPieceDisplayName("T", currInfo.isLight);
            desc += ` ${treeName} is now meadowed`;
          } else if (wasMeadowed && !isMeadowed) {
            const treeName = getPieceDisplayName("T", currInfo.isLight);
            desc += ` ${treeName} is no longer meadowed`;
          }
        }
      }
    }

    // Check for Snake shed respawn (Snake with shed ability was captured but respawned at back rank)
    const shedMarkers = ["*", "✳️", "✳"];
    for (const isLight of [true, false]) {
      // Find Snakes with shed in previous board
      const prevSnakesWithShed: { col: number; row: number; piece: string }[] = [];
      for (let col = 0; col < numCols; col++) {
        for (let row = 0; row < numRows; row++) {
          const square = prevBoard[col]?.[row];
          if (!square) continue;
          for (const piece of square) {
            const info = getPieceInfo(piece);
            if (info.type === "S" && info.isLight === isLight && shedMarkers.some((m) => piece.includes(m))) {
              prevSnakesWithShed.push({ col, row, piece });
            }
          }
        }
      }

      // Find Snakes without shed in current board (shed was used)
      const currSnakesWithoutShed: { col: number; row: number; piece: string }[] = [];
      for (let col = 0; col < numCols; col++) {
        for (let row = 0; row < numRows; row++) {
          const square = currBoard[col]?.[row];
          if (!square) continue;
          for (const piece of square) {
            const info = getPieceInfo(piece);
            if (info.type === "S" && info.isLight === isLight && !shedMarkers.some((m) => piece.includes(m))) {
              currSnakesWithoutShed.push({ col, row, piece });
            }
          }
        }
      }

      // If a Snake with shed disappeared and a Snake without shed appeared at a different position (back rank), it's a respawn
      for (const prevSnake of prevSnakesWithShed) {
        // Check if this snake's position is now empty or has a different piece (it was captured)
        const currSquare = currBoard[prevSnake.col]?.[prevSnake.row];
        const snakeStillThere = currSquare?.some((p) => {
          const info = getPieceInfo(p);
          return info.type === "S" && info.isLight === isLight;
        });

        if (!snakeStillThere) {
          // Snake was captured - look for respawn at back rank
          const backRank = isLight ? 0 : numRows - 1;
          for (const currSnake of currSnakesWithoutShed) {
            if (currSnake.row === backRank) {
              const snakeName = getPieceDisplayName("S", isLight);
              const respawnSquare = getAlgebraicNotation(currSnake.col, currSnake.row);
              desc += ` ${snakeName} used Shed and respawned [${respawnSquare}]`;
              break;
            }
          }
        }
      }
    }

    return formatDescription(desc);
  }

  // === CHECK FOR STATIONARY ENTANGLEMENT (Octopus entangles without moving) ===
  // This handles the edge case where an Octopus uses its entangle ability without moving
  const stationaryEntangle = detectStationaryEntanglement(
    prevBoard,
    currBoard,
    numCols,
    numRows
  );
  if (stationaryEntangle) {
    return formatDescription(stationaryEntangle);
  }

  // === FALLBACK: Single Piece Moved Heuristic ===
  // If no move was inferred, but there is exactly one piece of a given type and color that disappeared, and one that appeared, infer that as the move
  const prevPieceSet = new Set(
    prevPieces.map((p) => `${p.type}_${p.isLight}_${p.col}_${p.row}`)
  );
  const currPieceSet = new Set(
    currPieces.map((p) => `${p.type}_${p.isLight}_${p.col}_${p.row}`)
  );
  // Find pieces present in prev but not in curr (disappeared)
  const disappeared = prevPieces.filter(
    (p) => !currPieceSet.has(`${p.type}_${p.isLight}_${p.col}_${p.row}`)
  );
  // Find pieces present in curr but not in prev (appeared)
  const appeared = currPieces.filter(
    (p) => !prevPieceSet.has(`${p.type}_${p.isLight}_${p.col}_${p.row}`)
  );
  // Try to match by type and color
  for (const gone of disappeared) {
    const match = appeared.find(
      (a) => a.type === gone.type && a.isLight === gone.isLight
    );
    if (match) {
      // Compose a move description
      const actor = getPieceDisplayName(gone.type, gone.isLight);
      const fromSquare = getAlgebraicNotation(gone.col, gone.row);
      const toSquare = getAlgebraicNotation(match.col, match.row);
      // Penguin slides are cooler than moves - use "slid" for multi-square penguin moves
      const isPenguin = gone.type === "P";
      const distance = Math.max(
        Math.abs(match.col - gone.col),
        Math.abs(match.row - gone.row)
      );
      const moveVerb = isPenguin && distance > 1 ? "slid" : "moved";
      return formatDescription(`${actor} ${moveVerb} [${fromSquare} -> ${toSquare}]`);
    }
  }

  // Special case: Monkey failed leap (Monkey present in prevFEN, missing in currFEN, and #<roll> present)
  if (diceRoll !== null) {
    // Find all Monkeys in prevFEN
    let prevMonkeys: { col: number; row: number; isLight: boolean }[] = [];
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        for (const p of prevBoard[col][row]) {
          const info = getPieceInfo(p);
          if (info.type === "M") {
            prevMonkeys.push({ col, row, isLight: info.isLight ?? false });
          }
        }
      }
    }
    // Find all Monkeys in currFEN
    let currMonkeys: { col: number; row: number; isLight: boolean }[] = [];
    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < numRows; row++) {
        for (const p of currBoard[col][row]) {
          const info = getPieceInfo(p);
          if (info.type === "M") {
            currMonkeys.push({ col, row, isLight: info.isLight ?? false });
          }
        }
      }
    }
    // If a Monkey is missing after the move, and there was a roll, it's a failed leap
    if (prevMonkeys.length > currMonkeys.length) {
      // Find the missing Monkey (by position)
      const missingMonkey = prevMonkeys.find(
        (pm) =>
          !currMonkeys.some(
            (cm) =>
              cm.isLight === pm.isLight &&
              cm.col === pm.col &&
              cm.row === pm.row
          )
      );
      if (missingMonkey) {
        // Try to infer the leap destination: look for the farthest square in the same column or row with a piece removed
        // For now, just output the from square and a plausible to square (e.g., same col, farthest row)
        // Or, if there is only one Monkey in prev, and none in curr, just use the from square and a plausible to square
        const actor = getPieceDisplayName("M", missingMonkey.isLight);
        const fromSquare = getAlgebraicNotation(
          missingMonkey.col,
          missingMonkey.row
        );
        // Try to infer the to square: look for the farthest empty square in the same col or row
        let toSquare = fromSquare;
        // Try to find a plausible to square by looking for a square in prev that is now empty in curr
        for (let col = 0; col < numCols; col++) {
          for (let row = 0; row < numRows; row++) {
            const prevHasMonkey = prevBoard[col][row].some(
              (p) => getPieceInfo(p).type === "M"
            );
            const currHasMonkey = currBoard[col][row].some(
              (p) => getPieceInfo(p).type === "M"
            );
            if (
              !currHasMonkey &&
              prevHasMonkey &&
              (col !== missingMonkey.col || row !== missingMonkey.row)
            ) {
              toSquare = getAlgebraicNotation(col, row);
            }
          }
        }
        return formatDescription(
          `${actor} rolled a ${diceRoll} and failed leap`
        );
      }
    }
  }

  // Special case: Goat faint (Goat in prevFEN not fainted, in currFEN is fainted)
  for (let col = 0; col < numCols; col++) {
    for (let row = 0; row < numRows; row++) {
      const prevGoat = prevBoard[col][row].find(
        (p) => getPieceInfo(p).type === "G"
      );
      const currGoat = currBoard[col][row].find(
        (p) => getPieceInfo(p).type === "G"
      );
      if (prevGoat && currGoat) {
        // Check if fainted status changed
        const prevFainted = prevGoat.includes("$");
        const currFainted = currGoat.includes("$");
        if (!prevFainted && currFainted) {
          const actor = getPieceDisplayName(
            "G",
            getPieceInfo(currGoat).isLight
          );
          const square = getAlgebraicNotation(col, row);
          return formatDescription(`${actor} fainted [${square}]`);
        }
      }
    }
  }

  // --- SPECIAL CASE: Penguin rotate in place ---
  // If the only difference is a Penguin's direction at the same square, report it
  // (Do this after all other move/capture logic)
  const penguinDiffs: {
    col: number;
    row: number;
    prev: string;
    curr: string;
  }[] = [];
  for (let col = 0; col < numCols; col++) {
    for (let row = 0; row < numRows; row++) {
      if (
        !prevBoard[col] ||
        !prevBoard[col][row] ||
        !currBoard[col] ||
        !currBoard[col][row]
      )
        continue;
      const prevStack = prevBoard[col][row];
      const currStack = currBoard[col][row];
      if (prevStack.length === 1 && currStack.length === 1) {
        const prevPiece = prevStack[0];
        const currPiece = currStack[0];
        const prevInfo = getPieceInfo(prevPiece);
        const currInfo = getPieceInfo(currPiece);
        if (
          prevInfo.type === "P" &&
          currInfo.type === "P" &&
          prevInfo.isLight === currInfo.isLight &&
          prevPiece !== currPiece // Only the string (direction/modifiers) changed
        ) {
          penguinDiffs.push({ col, row, prev: prevPiece, curr: currPiece });
        }
      }
    }
  }
  if (penguinDiffs.length === 1) {
    // Only one penguin changed direction
    const { col, row, prev, curr } = penguinDiffs[0];
    // Try to extract direction symbol
    const dirMap: Record<string, string> = {
      "\u2191": "up", // ↑
      "\u2197": "up-right", // ↗
      "\u2192": "right", // →
      "\u2198": "down-right", // ↘
      "\u2193": "down", // ↓
      "\u2199": "down-left", // ↙
      "\u2190": "left", // ←
      "\u2196": "up-left", // ↖
    };
    // Find the direction symbol in the piece string (after 'P' or 'p')
    function extractDir(piece: string): string {
      // Match a Unicode arrow after P or p
      const match = piece.match(
        /^([Pp])([\u2191\u2197\u2192\u2198\u2193\u2199\u2190\u2196])/u
      );
      if (match) {
        const symbol = match[2];
        // Map the symbol to direction
        switch (symbol) {
          case "↑":
            return "up";
          case "↗":
            return "up-right";
          case "→":
            return "right";
          case "↘":
            return "down-right";
          case "↓":
            return "down";
          case "↙":
            return "down-left";
          case "←":
            return "left";
          case "↖":
            return "up-left";
          default:
            return symbol;
        }
      }
      return "unknown";
    }
    const prevDir = extractDir(prev);
    const currDir = extractDir(curr);
    const square = getAlgebraicNotation(col, row);

    // Check if penguin is zombified
    const prevInfo = getPieceInfo(prev);
    const currInfo = getPieceInfo(curr);
    const isZombified = currInfo.isZombified;

    // Build description
    let description = "";
    if (isZombified) {
      const penguinName = getPieceDisplayName("P", prevInfo.isLight);
      description = `Zombified ${penguinName} rotated ${currDir} in place [${square}]`;
    } else {
      description = `Penguin rotated ${currDir} in place [${square}]`;
    }

    return formatDescription(description);
  }

  return "Move could not be inferred";
}
